import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Badge, Spinner } from "../components/atoms";
import { ImageModal } from "../components/molecules";
import { fetchClient } from "../lib/openapi-query";
import type { ProductDetails, ProductVariant } from "../types/products";

export function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [product, setProduct] = useState<ProductDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(
    null
  );
  const [activeTab, setActiveTab] = useState<
    "description" | "ingredients" | "dosage" | "fda"
  >("description");
  const [showImageModal, setShowImageModal] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) {
        setError("Product ID is required");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await fetchClient.GET("/products/{productId}", {
          params: { path: { productId: id } },
        });

        if (response.error) {
          throw new Error("Failed to fetch product details");
        }

        const productData = response.data as unknown as ProductDetails;
        console.log('Product data received:', productData); // Debug log
        setProduct(productData);

        // Set first variant as selected by default
        if (productData.variants && productData.variants.length > 0) {
          console.log('Setting first variant:', productData.variants[0]); // Debug log
          setSelectedVariant(productData.variants[0]);
        }

        // Track page view with Google Analytics if source_id is present
        const source_id = searchParams.get('source_id');
        if (source_id) {
          console.log('Product viewed with source_id:', source_id, 'Product:', id);
          // Google Analytics tracking would go here
          // Example: gtag('event', 'page_view', { source_id: source_id, product_id: id });
          // For now, we're just logging it for demonstration
        }
      } catch (err) {
        console.error("Failed to fetch product:", err);
        setError(err instanceof Error ? err.message : "Failed to load product");
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id, searchParams]);

  const formatPrice = (amount: number | string, currency: string = "USD") => {
    // If already formatted as string (e.g., "$21.92"), return as-is
    if (typeof amount === "string") {
      return amount;
    }
    // Otherwise format the number
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Spinner size="xl" variant="primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-text-primary mb-2">
            Error Loading Product
          </h2>
          <p className="text-text-muted mb-4">{error}</p>
          <button
            onClick={() => navigate("/products")}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors"
          >
            Back to Products
          </button>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-text-primary mb-2">
            Product Not Found
          </h2>
          <p className="text-text-muted mb-4">
            The product you're looking for doesn't exist.
          </p>
          <button
            onClick={() => navigate("/products")}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors"
          >
            Back to Products
          </button>
        </div>
      </div>
    );
  }

  const displayPrice = selectedVariant?.price || product?.price || {};
  console.log('Display price:', displayPrice, 'Selected variant:', selectedVariant, 'Product:', product); // Debug log

  return (
    <div className="flex-1 overflow-auto bg-bg-secondary">
      <div className="max-w-6xl mx-auto p-6">
        {/* Back button */}
        <button
          onClick={() => navigate("/products")}
          className="mb-4 flex items-center text-text-muted hover:text-text-primary transition-colors"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to Products
        </button>

        {/* Product details card */}
        <div className="bg-bg-primary rounded-lg shadow-lg">
          <div className="grid md:grid-cols-2 gap-6 p-6">
            {/* Product image */}
            <div>
              {product.imageUrl ? (
                <div
                  className="w-full h-96 bg-bg-secondary rounded-lg overflow-hidden cursor-zoom-in group relative"
                  onClick={() => setShowImageModal(true)}
                >
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    className="w-full h-full object-contain transition-transform group-hover:scale-105"
                  />
                  {/* Zoom icon overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-3">
                      <svg
                        className="w-6 h-6 text-text-primary"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="w-full h-96 bg-bg-secondary rounded-lg flex items-center justify-center">
                  <span className="text-text-muted">No image available</span>
                </div>
              )}

              {/* Categories and Health Goals */}
              <div className="mt-4 space-y-2">
                {product.categories.length > 0 && (
                  <div>
                    <span className="text-sm font-semibold text-text-muted">
                      Categories:
                    </span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {product.categories.map((cat) => (
                        <Badge key={cat.id} variant="neutral" size="sm">
                          {cat.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {product.healthGoals && product.healthGoals.length > 0 && (
                  <div>
                    <span className="text-sm font-semibold text-text-muted">
                      Health Goals:
                    </span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {product.healthGoals.map((goal) => (
                        <Badge key={goal.id} variant="success" size="sm">
                          {goal.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Product info */}
            <div>
              <h1 className="text-3xl font-bold text-text-primary mb-2">
                {product.name}
              </h1>

              {product.sku && (
                <p className="text-sm text-text-muted mb-4">
                  SKU: {product.sku}
                </p>
              )}

              {/* Product Variants */}
              {product.variants && product.variants.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-text-primary mb-3">
                    Select Variant
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {product.variants.map((variant) => (
                      <button
                        key={variant.id}
                        onClick={() => setSelectedVariant(variant)}
                        className={`relative p-4 rounded-lg border-2 transition-all transform ${
                          selectedVariant?.id === variant.id
                            ? "border-primary bg-primary/10 shadow-lg scale-[1.02]"
                            : "border-border-medium hover:border-primary/50 hover:bg-bg-secondary"
                        } ${variant.price.inStock === false ? "opacity-60" : ""}`}
                        disabled={variant.price.inStock === false}
                      >
                        {/* Selected indicator */}
                        {selectedVariant?.id === variant.id && (
                          <div className="absolute top-2 right-2">
                            <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                              <svg
                                className="w-4 h-4 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="3"
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </div>
                          </div>
                        )}
                        
                        <div className="text-left">
                          <div className="font-semibold text-base text-text-primary">
                            {variant.name}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            {variant.strength && (
                              <span className="text-sm text-text-muted">
                                {variant.strength}
                              </span>
                            )}
                            {variant.strength && variant.size && (
                              <span className="text-text-muted">•</span>
                            )}
                            {variant.size && (
                              <span className="text-sm text-text-muted">
                                {variant.size}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-text-secondary mt-1">
                            SKU: {variant.sku}
                          </div>
                          {variant.price.inStock === false && (
                            <Badge variant="danger" size="sm" className="mt-2">
                              Out of Stock
                            </Badge>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Pricing Information */}
              {displayPrice && (
                <div className="mb-6 p-4 bg-bg-secondary rounded-lg">
                  <h3 className="text-lg font-semibold text-text-primary mb-3">
                    Pricing
                  </h3>
                  <div className="overflow-x-auto">
                    {Object.entries(displayPrice).filter(
                      ([_, value]) => value !== undefined && value !== null
                    ).length > 0 ? (
                      <table className="min-w-full">
                        <tbody className="divide-y divide-gray-100">
                          {Object.entries(displayPrice)
                            .filter(
                              ([_, value]) => value !== undefined && value !== null
                            )
                          .map(([key, value]) => {
                            // Format the key for display - preserve common abbreviations
                            const displayKey = (() => {
                              // Keep common abbreviations intact
                              const commonAbbreviations = ['PC', 'IBO', 'QV'];
                              if (commonAbbreviations.includes(key)) {
                                return key;
                              }
                              // For other keys, add spaces before capitals (but not for the first letter)
                              return key
                                .replace(/([a-z])([A-Z])/g, '$1 $2')
                                .replace(/^./, (str) => str.toUpperCase())
                                .trim();
                            })();

                            // Determine text color based on key
                            let valueClass = "text-gray-900";
                            if (
                              key.toLowerCase().includes("pc") ||
                              key.toLowerCase().includes("preferred")
                            ) {
                              valueClass = "text-green-600 font-semibold";
                            } else if (key.toLowerCase().includes("ibo")) {
                              valueClass = "text-blue-600 font-semibold";
                            } else if (
                              key.toLowerCase().includes("retail") ||
                              key === "price"
                            ) {
                              valueClass = "text-gray-900 font-bold text-lg";
                            } else if (key.toLowerCase().includes("qv")) {
                              valueClass = "text-purple-600 font-semibold";
                            }

                            // Format the value
                            let displayValue = value;
                            if (typeof value === "boolean") {
                              displayValue = value ? "Yes" : "No";
                            } else if (
                              typeof value === "number" &&
                              !key.toLowerCase().includes("qv") &&
                              !key.toLowerCase().includes("stock")
                            ) {
                              displayValue = formatPrice(
                                value,
                                displayPrice.currency || "USD"
                              );
                            }

                            return (
                              <tr key={key} className="hover:bg-gray-50">
                                <td className="py-2 pr-4 text-gray-600 font-medium whitespace-nowrap">
                                  {displayKey}:
                                </td>
                                <td className={`py-2 text-right ${valueClass}`}>
                                  {String(displayValue)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    ) : (
                      <p className="text-text-muted">No price information available</p>
                    )}
                  </div>
                </div>
              )}

              {/* Benefits */}
              {product.benefits && product.benefits.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-text-primary mb-2">
                    Benefits
                  </h3>
                  <ul className="list-disc list-inside text-text-secondary space-y-1">
                    {product.benefits.map((benefit, index) => (
                      <li key={index}>{benefit.title}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Packaging Information */}
              {product.packaging && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-text-primary mb-2">
                    Packaging
                  </h3>
                  <p className="text-text-secondary">{product.packaging}</p>
                </div>
              )}

              {/* Add to cart button */}
              <button
                className="w-full py-3 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={Boolean(
                  selectedVariant && selectedVariant.price.inStock === false
                )}
              >
                {selectedVariant && selectedVariant.price.inStock === false
                  ? "Out of Stock"
                  : "Add to Cart"}
              </button>
            </div>
          </div>

          {/* Tabs Section */}
          <div className="border-t">
            <div className="flex border-b">
              <button
                onClick={() => setActiveTab("description")}
                className={`px-6 py-3 font-medium transition-colors ${
                  activeTab === "description"
                    ? "text-primary border-b-2 border-primary"
                    : "text-text-muted hover:text-text-primary"
                }`}
              >
                Description
              </button>
              <button
                onClick={() => setActiveTab("ingredients")}
                className={`px-6 py-3 font-medium transition-colors ${
                  activeTab === "ingredients"
                    ? "text-primary border-b-2 border-primary"
                    : "text-text-muted hover:text-text-primary"
                }`}
              >
                Ingredients
              </button>
              <button
                onClick={() => setActiveTab("dosage")}
                className={`px-6 py-3 font-medium transition-colors ${
                  activeTab === "dosage"
                    ? "text-primary border-b-2 border-primary"
                    : "text-text-muted hover:text-text-primary"
                }`}
              >
                Dosage & Usage
              </button>
              <button
                onClick={() => setActiveTab("fda")}
                className={`px-6 py-3 font-medium transition-colors ${
                  activeTab === "fda"
                    ? "text-primary border-b-2 border-primary"
                    : "text-text-muted hover:text-text-primary"
                }`}
              >
                FDA & Safety
              </button>
            </div>

            <div className="p-6">
              {/* Description Tab */}
              {activeTab === "description" && (
                <div className="space-y-4">
                  {product.description && (
                    <div>
                      <h3 className="text-lg font-semibold text-text-primary mb-2">
                        Product Description
                      </h3>
                      <p className="text-text-secondary">
                        {product.description}
                      </p>
                    </div>
                  )}

                  {product.type && (
                    <div>
                      <h3 className="text-lg font-semibold text-text-primary mb-2">
                        Product Type
                      </h3>
                      <p className="text-text-secondary capitalize">
                        {product.type}
                      </p>
                    </div>
                  )}

                  {product.storage && (
                    <div>
                      <h3 className="text-lg font-semibold text-text-primary mb-2">
                        Storage Instructions
                      </h3>
                      <p className="text-text-secondary">{product.storage}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Ingredients Tab */}
              {activeTab === "ingredients" && (
                <div className="space-y-4">
                  {product.ingredients && product.ingredients.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-text-primary mb-3">
                        Active Ingredients
                      </h3>
                      <div className="grid md:grid-cols-2 gap-3">
                        {product.ingredients.map((ing, index) => (
                          <div
                            key={index}
                            className="flex items-center p-3 bg-bg-secondary rounded-lg"
                          >
                            {ing.imageUrl && (
                              <img
                                src={ing.imageUrl}
                                alt={ing.name}
                                className="w-10 h-10 rounded-full mr-3"
                              />
                            )}
                            <span className="text-text-primary">
                              {ing.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {product.nutritionalFacts && (
                    <div>
                      <h3 className="text-lg font-semibold text-text-primary mb-2">
                        Nutritional Information
                      </h3>
                      <p className="text-text-secondary">
                        {product.nutritionalFacts}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Dosage Tab */}
              {activeTab === "dosage" && product.dosage && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-text-primary mb-3">
                      Dosage Information
                    </h3>
                    <div className="space-y-3">
                      {product.dosage.recommended && (
                        <div>
                          <span className="font-medium text-text-primary">
                            Recommended Dosage:
                          </span>
                          <p className="text-text-secondary mt-1">
                            {product.dosage.recommended}
                          </p>
                        </div>
                      )}

                      {product.dosage.maximum && (
                        <div>
                          <span className="font-medium text-text-primary">
                            Maximum Daily Dose:
                          </span>
                          <p className="text-text-secondary mt-1">
                            {product.dosage.maximum.amount}{" "}
                            {product.dosage.maximum.unit}
                          </p>
                        </div>
                      )}

                      {product.dosage.instructions && (
                        <div>
                          <span className="font-medium text-text-primary">
                            Instructions:
                          </span>
                          <p className="text-text-secondary mt-1">
                            {product.dosage.instructions}
                          </p>
                        </div>
                      )}

                      {product.dosage.timing && (
                        <div>
                          <span className="font-medium text-text-primary">
                            Best Time to Take:
                          </span>
                          <p className="text-text-secondary mt-1">
                            {product.dosage.timing}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {product.measurementUnit && (
                    <div>
                      <span className="font-medium text-text-primary">
                        Measurement Unit:
                      </span>
                      <p className="text-text-secondary mt-1">
                        {product.measurementUnit}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* FDA Tab */}
              {activeTab === "fda" && (
                <div className="space-y-4">
                  {product.fdaDisclaimer && (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <h3 className="text-lg font-semibold text-amber-900 mb-2">
                        FDA Disclaimer
                      </h3>
                      <p className="text-amber-800 text-sm">
                        {product.fdaDisclaimer}
                      </p>
                    </div>
                  )}

                  {product.compatibility &&
                    product.compatibility.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-text-primary mb-2">
                          Compatibility Warnings
                        </h3>
                        <div className="space-y-2">
                          {product.compatibility.map((comp) => (
                            <div
                              key={comp.id}
                              className="p-3 bg-red-50 border border-red-200 rounded-lg"
                            >
                              <span className="font-medium text-red-900">
                                {comp.name}
                              </span>
                              {comp.description && (
                                <p className="text-red-700 text-sm mt-1">
                                  {comp.description}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  <div className="text-sm text-text-muted">
                    <p>
                      Always consult with a healthcare professional before
                      starting any new supplement regimen.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {product?.imageUrl && (
        <ImageModal
          isOpen={showImageModal}
          onClose={() => setShowImageModal(false)}
          imageUrl={product.imageUrl}
          alt={product.name}
        />
      )}
    </div>
  );
}
